// Card 35, ribbon banners (R548, ui-map-interface.md §17.3.35).
//
// An old chart floats a region's name on a ribbon scroll across the sea it
// names; a modern chart sets the name in spaced Title Case with a leader
// line to its centre, as the case rule of R1796 sets a name. Card 19
// named a ridge and a trench along their own shapes; this is the level above — the REGION, a stretch of water
// wide enough that "the Sisters are pushing into the Drifter Reach" is a
// sentence both readers can place.
//
// **THE OWNER'S RULING (2026-09-05).** Six ribbons, and every name comes
// from the region pool: one over each faction's founding water, and one at
// the middle of the map. The founding waters are `world.AnchorOf`, which
// `render/anchors.ts` mirrors, so a ribbon stands exactly where the server
// put the faction.
//
// The piece is TERRAIN furniture: it names the sea and not the season, so
// it draws in the `chartsea` tier and a replay film keeps it (R531).
//
// Yielding the ribbon to a hull under it is deferred (R548 Open).

import { ANCHOR_RADIUS_FRAC, FACTIONS } from '../../../gen/balance';
import { scrollRibbon } from '../../../render/chart-glyphs';
import { anchorOf } from '../../../render/anchors';
import { WORLD, WORLD_H } from '../../../world';
import type { FrameInfo } from '../../layer';
import type { ChartContext, ChartSinks, Piece } from '../context';
import { stepPx } from '../steps';
import { CHART_INK, faceOf, inkOf } from './shared';

/** One named region: where the ribbon floats and what it reads. */
interface Ribbon {
  x: number;
  y: number;
  name: string;
}

export class RibbonsPiece implements Piece {
  readonly id = 'ribbons';
  readonly tier = 'sea';
  readonly pack = 'static';
  readonly film = 'keep';
  readonly rebuildOn = ['hello', 'reading', 'font'] as const;

  /** The six ribbons of this world. The tests read them. */
  ribbons: Ribbon[] = [];

  /** No names, no ribbons: the pool is drawn at the hello. */
  visible(ctx: ChartContext): boolean {
    return ctx.names !== null;
  }

  rebuild(ctx: ChartContext): void {
    this.ribbons = [];
    const names = ctx.names?.regions ?? [];
    if (names.length === 0) return;
    const lift = ctx.cfg.pieces.ribbons.fig.lift;
    const n = FACTIONS.length;
    // One over every faction's founding water, then one at the middle.
    const at: { x: number; y: number }[] = [];
    for (let f = 1; f <= n; f++) {
      at.push(anchorOf(f, n, WORLD, WORLD_H, ANCHOR_RADIUS_FRAC));
    }
    at.push({ x: WORLD / 2, y: WORLD_H / 2 });
    this.ribbons = at
      .map((p, i) => ({ x: p.x, y: p.y - lift, name: names[i] ?? '' }))
      .filter((rb) => rb.name !== '');
  }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    // The static pack culls nothing: it outlives every pan (R2238).
    const t = ctx.cfg.pieces.ribbons;
    const hist = ctx.reading === 'historical';
    const r = t[ctx.reading];
    const face = faceOf(ctx.reading);
    const ink = inkOf(r.ink, CHART_INK.pale);
    for (const rb of this.ribbons) {
      if (hist) {
        const f = t.historical.fig;
        scrollRibbon(
          sinks.segments, rb.x, rb.y, f.scroll_w, f.scroll_h, ink, f.scroll_alpha, r.width,
        );
        // The name stands inside its drawn scroll, so it takes the size of
        // its step and grows with the water as the scroll does (R1796).
        ctx.glyphs.run(
          sinks.quads, rb.name, rb.x, rb.y, face, stepPx(face, t.text.main), ink, r.alpha,
          { anchor: 'centre' },
        );
        continue;
      }
      // The spaced name, a leader down to the water, and a disc on its foot.
      const f = t.modern.fig;
      ctx.glyphs.word(
        sinks.quads, rb.name, rb.x, rb.y, face, t.text.main, ink, f.caps_alpha,
        { anchor: 'centre', letterSpacing: f.caps_spacing },
      );
      const top = rb.y + f.leader_top;
      sinks.segments.line(
        rb.x, top, rb.x, top + f.leader_len, r.width, ink, f.leader_alpha,
      );
      sinks.circles.disc(rb.x, top + f.leader_len + f.disc_gap, f.disc_r, ink, r.alpha);
    }
  }
}

/** The registry's entry. */
export const ribbons = new RibbonsPiece();
